<script setup lang="ts">
import PropertiesPopupPanel from '@voix/components/chrome/PropertiesPopupPanel.vue'
import { computed, inject, onMounted, ref, watch } from 'vue'
import type { ComputedRef, Ref, WritableComputedRef } from 'vue'

import CheckboxInput from '../../../chrome/controls/CheckboxInput.vue'
import DropdownSelect from '../../../chrome/controls/DropdownSelect.vue'
import MediaDirectories from './MediaDirectories.vue'
import ProviderSelector from './ProviderSelector.vue'
import MediaSelect from './MediaSelect.vue'
import LocalFileSelector from './LocalFileSelector.vue'
import { useCookie, useNuxtApp } from '#imports'

const emit = defineEmits(['close'])

const { $voix } = useNuxtApp()
const mode = inject<'sidebar' | 'modal'>('mediaManagerDisplayMode', 'sidebar')
const selectedDirectory = ref('')
const searchTerm = ref('')

const provider: Ref<string | null> = inject('provider', ref(null))

const directoryMode = computed(() => {
  if (provider.value && $voix.options.mediaProviderConfig[provider.value]?.hierarchyMode)
    return $voix.options.mediaProviderConfig[provider.value].hierarchyMode

  else
    return 'tags'
})

const breadCrumbDirectories: { value: Array<{ title: string, id: string }> } = useCookie('voix-media-breadcrumb-directories')
if (!breadCrumbDirectories.value || typeof breadCrumbDirectories.value !== 'object')
  breadCrumbDirectories.value = []

const rememberMediaProvider: { value: string } = useCookie('voix-media-provider')
const rememberMediaDirectory: { value: string } = useCookie('voix-media-directory')
const shouldRememberMediaDirectory: WritableComputedRef<boolean> = computed({
  get: () => {
    if (rememberMediaProvider.value)
      return true

    else
      return false
  },
  set: (value) => {
    if (value && provider.value) {
      rememberMediaProvider.value = provider.value
      rememberMediaDirectory.value = selectedDirectory.value
    }

    else {
      rememberMediaProvider.value = ''
      rememberMediaDirectory.value = ''
    }
  },
})

function selectDirectory(directory: { title: string, id: string }) {
  searchTerm.value = ''
  if (directory?.id) {
    if (shouldRememberMediaDirectory.value) {
      rememberMediaDirectory.value = directory.id
      if (provider.value)
        rememberMediaProvider.value = provider.value
    }

    breadCrumbDirectories.value.push(directory)
    selectedDirectory.value = directory.id
  }
  else {
    goToHomeDirectory()
  }
}

function refreshDirectory() {
  if (selectedDirectory.value)
    selectDirectory({ title: '', id: selectedDirectory.value })
}

function goToHomeDirectory() {
  rememberMediaDirectory.value = ''
  breadCrumbDirectories.value = []
  selectedDirectory.value = ''
}

watch(provider, (newValue, oldValue) => {
  if (provider.value)
    rememberMediaProvider.value = provider.value
  if (oldValue)
    goToHomeDirectory()
})

const breadcrumbOptions: ComputedRef<Array<{ label: string, value: string | null, key: string }>> = computed(() => {
  const options = [{
    label: 'Home',
    value: '',
    key: '',
  }]
  if (breadCrumbDirectories.value) {
    options.push(...breadCrumbDirectories.value.map((directory: any) => {
      return {
        label: directory.title,
        value: directory.id,
        key: directory.id,
      }
    }))
  }

  return options
})

function selectFromBreadcrumbs(directoryId: string) {
  if (directoryId) {
    const selectedBreadcrumbDirectory = breadCrumbDirectories.value?.find(directory => directory.id === directoryId)
    if (selectedBreadcrumbDirectory) {
      const index = breadCrumbDirectories.value.indexOf(selectedBreadcrumbDirectory)
      breadCrumbDirectories.value = breadCrumbDirectories.value.slice(0, index + 1)
      selectedDirectory.value = selectedBreadcrumbDirectory.id

      if (shouldRememberMediaDirectory.value) {
        rememberMediaDirectory.value = selectedDirectory.value
        if (provider.value)
          rememberMediaProvider.value = provider.value
      }
    }
  }
  else {
    goToHomeDirectory()
  }
}

onMounted(() => {
  if (rememberMediaProvider.value)
    provider.value = rememberMediaProvider.value

  if (rememberMediaDirectory.value) {
    selectedDirectory.value = rememberMediaDirectory.value ? rememberMediaDirectory.value : ''

    if (rememberMediaDirectory.value && typeof rememberMediaDirectory.value === 'object')
      breadCrumbDirectories.value = rememberMediaDirectory.value
  }
})
</script>

<template>
  <teleport to="#voix-admin-root">
    <div class="absolute z-[60] inset-0 text-admin-gray-800">
      <div class="relative flex items-stretch h-full">
        <div class="flex flex-col border-r border-gray-100 w-[300px] bg-gray-50">
          <ProviderSelector />

          <template v-if="directoryMode === 'directories' || directoryMode === 'tags'">
            <div class="flex space-x-2 voix-admin-bg-lightest px-4 py-2 border-b voix-admin-border-lightest">
              <CheckboxInput
                :selected="shouldRememberMediaDirectory"
                @click="shouldRememberMediaDirectory = !shouldRememberMediaDirectory"
              />
              <span class="text-2xs font-bold uppercase voix-admin-text-dark">Remember Location</span>
            </div>

            <button
              class="mt-4 bg-gray-200 text-gray-500 p-2.5 px-2.5 rounded flex items-center text-xs  w-full"
              @click="goToHomeDirectory"
            >
              <Icon name="heroicons:home-20-solid" class="w-4 h-4 mr-1" />
              Go Home
            </button>

            <DropdownSelect
              id="media-manager-breadcrumb-selector" :model-value="selectedDirectory" class="m-2"
              :options="breadcrumbOptions" placeholder="Breadcrumbs" @update:model-value="selectFromBreadcrumbs"
            />

            <MediaDirectories
              :selected-directory="selectedDirectory" :directory-mode="directoryMode"
              @select-directory="selectDirectory" @refresh-directory="refreshDirectory"
            />
          </template>

          <button class="voix-admin-bg-light voix-admin-text-dark py-3 rounded-sm text-xs flex items-center justify-center space-x-1" @click="$emit('close')">
            <Icon name="heroicons:x-mark-20-solid" class="w-5 h-5" />
            <span>Close Media Manager</span>
          </button>
        </div>

        <MediaSelect
          v-if="directoryMode !== 'local'" v-model:search-term="searchTerm"
          :selected-directory="selectedDirectory" :directory-mode="directoryMode" class="flex-1"
        />

        <div
          v-else-if="directoryMode === 'local'" class="flex-1 bg-white h-[100vh] p-8"
        >
          <LocalFileSelector />
        </div>
      </div>
    </div>
  </teleport>
</template>
